import type { Ref, RefObject } from 'react';
import { vegaArtifactGraph } from '@gonfalon/dogfood-flags/dogfood-flags';
import { canonicalizeArtifactExternalId } from '@gonfalon/observability-ui/components/Artifact';
import {
  type ArtifactLookupEntry,
  ArtifactLookupProvider,
} from '@gonfalon/observability-ui/components/MarkdownRenderer';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';

import { useAgentInsights } from '../../AgentInsightsContext';
import { useAiChatLimit } from '../../hooks/useAiChatLimit';
import {
  useVegaAccessDenied,
  VegaPermissionDeniedAlert,
} from '../../internal/components/AgentInsights/VegaPermissionDenied';

import { AgentInsights } from './AgentInsights';
import type { AgentPromptMutationFn } from './AgentInsights';
import { VegaConversationTabs } from './VegaConversationTabs';
import type { VegaPromptComposerHandle } from './VegaPromptComposer';

const EMPTY_ARTIFACT_LOOKUP: ArtifactLookupEntry[] = [];

export interface VegaConversationProps {
  agentPromptMutation: AgentPromptMutationFn;
  sending: boolean;
  enableAgentResponseSelector?: boolean;
  /** Hides the composer's gen-AI chrome for skills that route to a gate-exempt endpoint. */
  simplifiedFooter?: boolean;
  isLoadingConversation?: boolean;
  /** Panel-style hosts wire these to their own header actions; inline hosts can skip them. */
  onClearRef?: RefObject<(() => void) | null>;
  onFocusRef?: RefObject<(() => void) | null>;
  onClearPromptRef?: RefObject<(() => void) | null>;
  /** Forwarded to the composer. See VegaPromptComposerHandle. */
  composerRef?: Ref<VegaPromptComposerHandle>;
}

/**
 * The conversation surface on its own — the message list, composer, and artifact tabs — without any
 * of the drawer chrome that `AgentInsightsPanel` puts around it.
 *
 * Pulled out so a host can put a chat somewhere other than the drawer (an onboarding step, say)
 * without recreating the permission gate, the chat-limit wiring, and the artifact-graph tab shell.
 * Conversation state still comes from `AgentInsightsProvider`, so a host seeds the prompt with
 * `configureConversation` and reads `currentConversationId` from the same context.
 */
export function VegaConversation({
  agentPromptMutation,
  sending,
  enableAgentResponseSelector,
  simplifiedFooter = false,
  isLoadingConversation = false,
  onClearRef,
  onFocusRef,
  onClearPromptRef,
  composerRef,
}: VegaConversationProps) {
  const { projectId, projectKey } = useProjectId();
  const { currentConversationId } = useAgentInsights();
  const { data: aiChatLimitData } = useAiChatLimit();
  const accessDenied = useVegaAccessDenied();

  if (accessDenied) {
    return <VegaPermissionDeniedAlert />;
  }

  const insights = (
    <AgentInsights
      onClearRef={onClearRef}
      onFocusRef={onFocusRef}
      onClearPromptRef={onClearPromptRef}
      composerRef={composerRef}
      isLoadingConversation={isLoadingConversation}
      aiChatLimitData={aiChatLimitData}
      simplifiedFooter={simplifiedFooter}
      agentPromptMutation={agentPromptMutation}
      sending={sending}
      enableAgentResponseSelector={enableAgentResponseSelector}
    />
  );

  if (!vegaArtifactGraph()) {
    return insights;
  }

  // Same Messages · Artifacts tabs as the full-page conversation view. Always wrap when the flag is
  // on (even for an unsaved chat) so AgentInsights stays mounted when a conversation id arrives
  // mid-session — swapping the tree later would remount the chat and drop in-flight streaming and
  // composer state. Artifacts queries already no-op for `new`.
  const conversationIdForTabs =
    currentConversationId && currentConversationId !== 'new' ? currentConversationId : 'new';

  return (
    <ArtifactLookupProvider
      projectKey={projectKey}
      artifacts={EMPTY_ARTIFACT_LOOKUP}
      canonicalize={canonicalizeArtifactExternalId}
    >
      <VegaConversationTabs
        projectId={projectId}
        projectKey={projectKey}
        conversationId={conversationIdForTabs}
        messages={insights}
        syncTabToSearchParams={false}
      />
    </ArtifactLookupProvider>
  );
}
